<script setup lang="ts">
import { onMounted, shallowRef, ref } from "vue";
import { AgGridVue } from "ag-grid-vue3";
import {
  themeQuartz,
  type GridApi,
  type GridOptions,
  type GridReadyEvent,
} from "ag-grid-enterprise";
import {useCharterFinancialDataStore} from "@/charter/stores/charter-financial-data-store.ts";
import {
  GetFinancialDataRequestBuilderGetQueryParameters
} from "@/api/client/charter/getFinancialData/index.ts";

const charterFinancialDataStore = useCharterFinancialDataStore();

const myTheme = themeQuartz.withParams({});

// Filter form state
const filterParams = ref<GetFinancialDataRequestBuilderGetQueryParameters>({
  route: "",
  aircraftTypeId: 0,
  isFerry: false,
  status: "",
  flightNumber: "",
  clientId: 0,
  year: new Date().getFullYear(),
  month: new Date().getMonth() + 1,
  flightType: "",
  seasonId: 0,
});

const gridOptions: GridOptions = {
  pagination: true,
  autoSizeStrategy: {
    type: "fitGridWidth",
    defaultMinWidth: 100,
  },
  defaultColDef: {
    resizable: false,
    filter: true,
  },
  columnDefs: [
    { field: "flightNumber", headerName: "Flight Number" },
    { field: "route", headerName: "Route" },
    { field: "aircraftType", headerName: "Aircraft Type" },
    { field: "client", headerName: "Client" },
    { field: "status", headerName: "Status" },
    { field: "departureDate", headerName: "Departure Date" },
    { field: "revenue", headerName: "Revenue" },
    { field: "cost", headerName: "Cost" },
    { field: "profit", headerName: "Profit" },
    { field: "profitMargin", headerName: "Profit Margin (%)" },
  ],
};

const gridApi = shallowRef<GridApi | null>(null);

onMounted(async () => {
  await fetchData();
});

function onGridReady(params: GridReadyEvent) {
  gridApi.value = params.api;
}

function fetchData() {
  charterFinancialDataStore.fetchFinancialData(filterParams.value);
}

function applyFilters() {
  fetchData();
}

function resetFilters() {
  filterParams.value = {
    route: "",
    aircraftTypeId: 0,
    isFerry: false,
    status: "",
    flightNumber: "",
    clientId: 0,
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    flightType: "",
    seasonId: 0,
  };
  fetchData();
}

function exportData() {
  const date = new Date();
  gridApi.value!.exportDataAsExcel({
    fileName: "charter-financial-data_" + date.toLocaleString()
  });
}
</script>

<template>
  <div class="flex h-full flex-col gap-4">
    <div class="flex items-center justify-between">
      <h1>Reporting</h1>
      <div class="flex gap-2">
        <p-button link @click="fetchData" :loading="charterFinancialDataStore.isLoading">
          <span class="material-icons" title="refresh">refresh</span>
        </p-button>
        <p-button link @click="exportData" :disabled="!charterFinancialDataStore.hasFinancialData">
          <span class="material-icons" title="download">download</span>
        </p-button>
      </div>
    </div>

    <!-- Filter Form -->
    <p-card class="filter-card">
      <template #title>
        <div class="flex items-center justify-between gap-4">
          <span>Filters</span>
          <div class="flex gap-2">
            <p-button
              label="Apply"
              icon="pi pi-search"
              @click="applyFilters"
              :loading="charterFinancialDataStore.isLoading"
            />
            <p-button
              label="Reset"
              icon="pi pi-refresh"
              severity="secondary"
              outlined
              @click="resetFilters"
            />
          </div>
        </div>
      </template>
      <template #content>
        <div class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <div>
            <span class="p-float-label block">
              <p-input-text id="flightNumber" v-model="filterParams.flightNumber" class="w-full" />
              <label for="flightNumber">Flight Number</label>
            </span>
          </div>
          <div>
            <span class="p-float-label block">
              <p-input-text id="route" v-model="filterParams.route" class="w-full" />
              <label for="route">Route</label>
            </span>
          </div>
          <div>
            <span class="p-float-label block">
              <p-input-text id="status" v-model="filterParams.status" class="w-full" />
              <label for="status">Status</label>
            </span>
          </div>
          <div>
            <span class="p-float-label block">
              <p-input-text id="flightType" v-model="filterParams.flightType" class="w-full" />
              <label for="flightType">Flight Type</label>
            </span>
          </div>
          <div>
            <span class="p-float-label block">
              <p-input-number id="year" v-model="filterParams.year" class="w-full" />
              <label for="year">Year</label>
            </span>
          </div>
          <div>
            <span class="p-float-label block">
              <p-input-number id="month" v-model="filterParams.month" class="w-full" min="1" max="12" />
              <label for="month">Month</label>
            </span>
          </div>
          <div>
            <span class="p-float-label block">
              <p-input-number id="aircraftTypeId" v-model="filterParams.aircraftTypeId" class="w-full" />
              <label for="aircraftTypeId">Aircraft Type ID</label>
            </span>
          </div>
          <div>
            <span class="p-float-label block">
              <p-input-number id="clientId" v-model="filterParams.clientId" class="w-full" />
              <label for="clientId">Client ID</label>
            </span>
          </div>
          <div>
            <span class="p-float-label block">
              <p-input-number id="seasonId" v-model="filterParams.seasonId" class="w-full" />
              <label for="seasonId">Season ID</label>
            </span>
          </div>
          <div class="flex items-center">
            <p-checkbox v-model="filterParams.isFerry" binary id="isFerry" />
            <label for="isFerry" class="ml-2">Is Ferry</label>
          </div>
        </div>
      </template>
    </p-card>

    <AgGridVue
      :rowData="charterFinancialDataStore.financialData"
      :loading="charterFinancialDataStore.isLoading"
      :gridOptions="gridOptions"
      ref="gridRef"
      :theme="myTheme"
      style="width: 100%; height: 100%"
      @grid-ready="onGridReady"
    />
  </div>
</template>

<style scoped lang="scss">
.sn-message-text {
  font-size: 1rem !important;
}

h1 {
  margin: 0 !important;
}

.p-button {
  min-height: fit-content !important;
}

.p-button-icon-left {
  margin-right: 0.4375rem !important;
}

.p-button-label {
  font-weight: 700 !important;
}

.p-button-outlined {
  background-color: white !important;
  color: rgb(30, 41, 59) !important;
  font-size: 14px;
  line-height: 21px !important;
  padding: 6px 10px;
  border-radius: 6px;
  border-color: #e2e8f0 !important;
}

.p-button-outlined:hover {
  background-color: var(--p-button-outlined-secondary-hover-background) !important;
}

.p-button-outlined:active {
  background-color: var(--p-button-outlined-secondary-active-background) !important;
}

.p-message {
  margin: 0;
}

.filter-card {
  margin-bottom: 0.625rem;
}

.p-float-label {
  margin-bottom: 0.625rem;
}
</style>
